// BrandContext — everything the module registry needs to render a brand's home,
// resolved once from a brand token. This is the "brands are just data" seam: point
// it at a different token and every module re-renders as that brand. Product-set
// selection logic lives here (ported from Page.tsx) so modules stay declarative.
import type { FaireReviewEntity } from "@faire/playground-entity-db/entity_types/review";
import {
  getBrandReviews,
  getReviewsForProduct,
} from "@faire/prototype-playground/data/entity_sets/reviewsDatabase";
import {
  getEntityByToken,
  getProductsByBrandToken,
} from "@faire/prototype-playground/data/get_entity";

import { getBetterTogether } from "./betterTogether";
import { type PriceMode } from "./PriceLabel";
import { type ProductCardData } from "./ProductCard";
import { pickBestReview, scoreReview } from "./reviewQuality";
import { type BrandDataset, type StarterProduct } from "./types";

export type CatalogProduct = ReturnType<typeof getProductsByBrandToken>[number];

export interface BrandContext {
  dataset: BrandDataset;
  isLoggedOut: boolean;
  priceMode: PriceMode;
  /** Pre-resolved product-card sets the modules select from (keyed by the named
   * string selectors; category selectors resolve via cardsByCategory). */
  cards: Record<string, ProductCardData[]>;
  /** First catalog image whose name matches one of the keywords. */
  pickImage: (keywords: string[]) => string | undefined;
  /** Product cards for a category-tile label (used by the personalization signal). */
  cardsByCategory: (label: string) => ProductCardData[];
  /** The brand's full catalog (isBestseller already resolved against
   * bestsellerNameHints) — for an "All Products" grid, not the curated
   * home-module pools above. */
  allProducts: CatalogProduct[];
  /** Real brand-level reviews (see `getBrandReviews`) — a shopper's overall
   * review of the brand, not tied to one product. Feeds the Reviews module's
   * card row alongside its per-product quote cards. Empty for brands with
   * none imported yet. */
  brandReviews: FaireReviewEntity[];
  /** The reviews the BRAND itself curated, resolved from
   * `BrandDataset.featuredReviewTokens` and kept in the brand's order (not
   * re-sorted by date or rating — the order IS the curation). Empty when the
   * brand hasn't picked any, which makes resolveIA skip the module. */
  featuredReviews: FaireReviewEntity[];
}

const toCard = (product: CatalogProduct): ProductCardData => ({
  token: product.token,
  name: product.name,
  imageUrl: product.imageUrl,
  galleryImageUrls: product.galleryImageUrls,
  priceDollars: product.priceDollars,
  msrpDollars: product.msrpDollars,
  caseSize: product.caseSize,
  isBestseller: product.isBestseller,
});

/**
 * Brands that aren't in entity-db yet still need a catalog. `starterProducts`
 * on the dataset is that stand-in — same card shape the rest of the pipeline
 * already consumes, so a brand can ship a home before its tokens are imported.
 */
const catalogFromStarter = (
  starter: StarterProduct,
  dataset: BrandDataset,
  brandToken: string
): CatalogProduct => ({
  token: starter.token,
  type: "product",
  name: starter.name,
  brandName: dataset.name,
  brandToken,
  imageUrl: starter.imageUrl ?? "",
  description: "",
  tags: [],
  priceDollars: starter.priceDollars ?? 0,
  msrpDollars: starter.msrpDollars,
  caseSize: starter.caseSize,
  freeShipping: starter.freeShipping,
  isBestseller: starter.isBestseller,
  inStock: true,
  meta: {
    source: "mock",
    importedAt: "2026-09-28T00:00:00.000Z",
    importedBy: "brand-dataset",
  },
});

export function buildBrandContext(
  dataset: BrandDataset,
  opts: { brandToken: string; isLoggedOut: boolean }
): BrandContext {
  // Bestseller flag: honor the entity's own signal, plus an optional brand-
  // supplied name-hint list (mock "performance" signal for brands without real
  // ranking data yet — omit entirely once real data exists).
  const bestsellerNameHints = dataset.bestsellerNameHints ?? [];
  const importedProducts = getProductsByBrandToken(opts.brandToken);
  const catalogSource =
    importedProducts.length > 0
      ? importedProducts
      : (dataset.starterProducts ?? []).map((starter) =>
          catalogFromStarter(starter, dataset, opts.brandToken)
        );
  const products = catalogSource.map((product) => {
    const lower = product.name.toLowerCase();
    return {
      ...product,
      isBestseller:
        product.isBestseller ||
        bestsellerNameHints.some((keyword) => lower.includes(keyword)),
    };
  });

  // Cross-module dedup: each pool below is claimed in roughly page order (top to
  // bottom), and every later pool excludes tokens an earlier pool already used.
  // A thin catalog means later pools naturally end up smaller (or empty) rather
  // than repeating a product the retailer already saw in an earlier module —
  // resolveIA then drops any module whose pool came up too small to bother with.
  const usedTokens = new Set<string>();
  const claim = (cards: ProductCardData[]): ProductCardData[] => {
    cards.forEach((card) => usedTokens.add(card.token));
    return cards;
  };
  const unclaimed = (product: CatalogProduct): boolean =>
    !usedTokens.has(product.token);

  // "Top Sellers" — real bestseller-tagged products, claimed before anything
  // else so a brand with a thin bestseller pool doesn't lose most of it to an
  // earlier collection module's keyword match (e.g. a "Frozen Cocktails"
  // collection matching 3 of a brand's only 5 real bestsellers before this
  // pool gets a turn). Opt-in — see `BrandDataset.topSellersModule`. Brands
  // with no real bestseller signal (`hasBestsellers: false`) fall back to a
  // general recent/in-stock pool instead of faking a bestseller flag — same
  // prominent lead-in treatment, honest data underneath (resolveIA titles it
  // "Top Reordered Products" in that case).
  const topSellers: ProductCardData[] = dataset.topSellersModule
    ? claim(
        (dataset.hasBestsellers
          ? products.filter(
              (product) => product.isBestseller && unclaimed(product)
            )
          : products.filter((product) => product.imageUrl && unclaimed(product))
        )
          .slice(0, 12)
          .map(toCard)
      )
    : [];

  // Starter Kit / sampler — one product from each of the top category tiles, so
  // a new retailer gets a cross-category "sampler" of the range as one bundle.
  // Claimed first: it's the very first product content a new retailer sees.
  const starterKit: ProductCardData[] = claim(
    dataset.categoryTiles
      .slice(0, 5)
      .map((tile) => {
        const overrideToken = dataset.starterKitOverrides?.[tile.label];
        const match = products.find((product) => {
          if (!product.imageUrl || !unclaimed(product)) return false;
          return overrideToken
            ? product.token === overrideToken
            : tile.keywords.some((keyword) =>
                product.name.toLowerCase().includes(keyword)
              );
        });
        return match ? toCard(match) : null;
      })
      .filter((card): card is ProductCardData => card !== null)
  );

  // Top category tile (e.g. Candles) as a featured-collection product set.
  const topCategory = dataset.categoryTiles[0];
  const topCategoryCards: ProductCardData[] = claim(
    topCategory
      ? products
          .filter(
            (product) =>
              product.imageUrl &&
              unclaimed(product) &&
              topCategory.keywords.some((keyword) =>
                product.name.toLowerCase().includes(keyword)
              )
          )
          .slice(0, 6)
          .map(toCard)
      : []
  );

  const pickedForYou = claim(
    products
      .filter((product) => product.imageUrl && unclaimed(product))
      .slice(-12)
      .map(toCard)
  );

  // "Tried & True" — brand-flagged signature picks (any SKU whose name matches
  // one of the brand's named flagship products) ahead of generic bestsellers.
  // Falls back to bestsellers alone when the brand hasn't curated any picks.
  const signaturePool = (dataset.signaturePicks ?? []).flatMap((pick) => {
    const lowerPick = pick.toLowerCase();
    return products.filter(
      (product) =>
        unclaimed(product) && product.name.toLowerCase().includes(lowerPick)
    );
  });
  const triedAndTruePool = [
    ...signaturePool,
    ...products.filter((product) => product.isBestseller && unclaimed(product)),
  ];
  const triedAndTrue = claim(
    triedAndTruePool
      .filter(
        (product, index, list) =>
          list.findIndex((other) => other.token === product.token) === index
      )
      .slice(0, 6)
      .map(toCard)
  );

  // Generic bestsellers — feeds "Shop the latest", a shoppable merchandising
  // row. Claimed last of the real-product pools, since it's the one most
  // likely to run thin on a small catalog once everything above has already
  // claimed its share; resolveIA gates this module on the pool's actual size.
  const bestsellers = products
    .filter((product) => product.isBestseller && unclaimed(product))
    .slice(0, 12)
    .map(toCard);

  // "Highly rated" and the aggregate "Rating & Reviews" module both pull
  // quotes from real per-product reviews (see `getReviewsForProduct`) rather
  // than a flat, brand-agnostic mock list — each draws a different slice of
  // this pool so the two modules never show an identical quote twice on one
  // page. Like the pools above, NOT deduped against usedTokens: a review
  // quote about a product already featured elsewhere on the page reinforces
  // trust rather than reading as a repeat, unlike a merchandising row
  // repeating the same shoppable tile.
  // Ordered by how good the product's best review is, not by bestseller rank:
  // the modules downstream take the front of this pool, so the brand's best
  // quotes have to be at the front of it. Products whose reviews are all too
  // long to sit in a tile drop out here, which is why this filters on
  // `pickBestReview` rather than on a review merely existing (Ryan,
  // 2026-09-17).
  const reviewedProducts = products
    .filter((product) => product.imageUrl)
    .map((product) => ({
      product,
      review: pickBestReview(getReviewsForProduct(product.token)),
    }))
    .filter(
      (
        entry
      ): entry is {
        product: (typeof entry)["product"];
        review: FaireReviewEntity;
      } => entry.review !== undefined
    )
    .sort((left, right) => scoreReview(right.review) - scoreReview(left.review))
    .slice(0, 12)
    .map((entry) => toCard(entry.product));
  const highlyRatedProducts = reviewedProducts.slice(0, 4);
  // Whatever's left after "Highly rated" claims its 4. Only reuse the front
  // of the pool when "Highly rated" itself has too few reviews to render
  // (resolveIA gates it on >= 3) — otherwise the same quote cards would show
  // twice on one page, once in each module.
  const remainingReviewed = reviewedProducts.slice(4);
  const reviewProducts = (
    remainingReviewed.length > 0 || highlyRatedProducts.length >= 3
      ? remainingReviewed
      : reviewedProducts
  ).slice(0, 3);

  // Optional brand-curated seasonal/limited collection (e.g. PF Candle's "Fall
  // Classics"), matched by literal product-name substrings. Empty when the
  // brand hasn't curated one — resolveIA skips the module entirely in that case.
  const seasonalImagePool = products.filter((product) => product.imageUrl);
  const fallClassics: ProductCardData[] = (
    dataset.seasonalCollection?.productNames ?? []
  ).map((name, index) => {
    const base =
      seasonalImagePool[index % Math.max(seasonalImagePool.length, 1)] ??
      products[0];
    return {
      token: `seasonal-${index}`,
      name,
      imageUrl: base?.imageUrl,
      priceDollars: base?.priceDollars,
      msrpDollars: base?.msrpDollars,
      caseSize: base?.caseSize,
      isBestseller: false,
    };
  });

  const pickImage = (keywords: string[]): string | undefined =>
    products.find(
      (product) =>
        product.imageUrl &&
        keywords.some((keyword) => product.name.toLowerCase().includes(keyword))
    )?.imageUrl;

  const cardsByCategory = (label: string): ProductCardData[] => {
    const tile = dataset.categoryTiles.find((t) => t.label === label);
    const keywords = tile?.keywords ?? [label.toLowerCase()];
    return products
      .filter(
        (product) =>
          product.imageUrl &&
          keywords.some((keyword) =>
            product.name.toLowerCase().includes(keyword)
          )
      )
      .slice(0, 12)
      .map(toCard);
  };

  return {
    dataset,
    isLoggedOut: opts.isLoggedOut,
    priceMode: opts.isLoggedOut ? "msrp-only" : "full",
    cards: {
      bestsellers,
      highlyRatedProducts,
      reviewProducts,
      pickedForYou,
      triedAndTrue,
      fallClassics,
      topCategory: topCategoryCards,
      starterKit,
      topSellers,
      // Cross-brand complementary recs (real products from other brands).
      // Competitor keywords default to this brand's own category-tile
      // vocabulary when the brand hasn't curated an explicit list.
      betterTogether: getBetterTogether({
        brandName: dataset.name,
        competitorKeywords:
          dataset.competitorKeywords ??
          dataset.categoryTiles.flatMap((tile) => tile.keywords),
        complementaryKeywords: dataset.complementaryKeywords ?? [],
      }),
    },
    pickImage,
    cardsByCategory,
    allProducts: products,
    brandReviews: getBrandReviews(opts.brandToken),
    // Brand-curated, so preserve the dataset's order rather than sorting —
    // and drop any token with no imported review instead of substituting one.
    featuredReviews: (dataset.featuredReviewTokens ?? [])
      .map((token) => getEntityByToken(token, "review"))
      .filter((review): review is FaireReviewEntity => review !== undefined),
  };
}
